import {Injectable} from '@angular/core';
import {Action, Selector, State, StateContext, Store} from '@ngxs/store';
import {BaseService, DataBaseServiceResponse} from '../../services/base-service/base.service';
import {environment} from '../../../environments/environment';
import {AppointmentStatusEnum} from './appointment-status.enum';
import {IAppointment} from './appointment.interface';
import {ScheduleAppointmentRequestDTO} from '../../dtos/service';
import {UtilitiesService} from '../../services/utilities-service/utilities.service';
import {cloneDeep} from 'lodash';
import {MealPlanAppointmentResponseDTO} from '../../dtos/meal-plan';
import {firstValueFrom} from 'rxjs';
import {Params} from '@angular/router';
import {IDateInterface, IGeneratedBlocks} from '../../interfaces/date';
import {DateHelper} from '../../class/helper/date.helper.class';
import {UserService} from '../../services/user/user.service';

export interface IRAppointment {
  appointments: IAppointment[];
  appointmentsToMealPlan: MealPlanAppointmentResponseDTO[];
  appointmentsToAssignTo: MealPlanAppointmentResponseDTO[];
  selectedAppointment?: string;
  selectedAppointmentObjectives?: string;
  scheduleAvailability: IGeneratedBlocks;
  nextAppointmentPatient?: IAppointment;
  appointmentURL: IAppointment;
}

export namespace AppointmentActions {
  export class Get {
    static readonly type: string = '[Appointment] Get All appointments';
    constructor(
      public clinicId: string,
      public patientOrExpertId: string,
      public isExpert: boolean,
      public year: number,
      public month: number,
      public status?: AppointmentStatusEnum[]
    ) { }
  }

  export class GetToAssign {
    static readonly type: string = '[Appointment] Get Appointments To Assign Action';
    constructor(public clinicId: string, public serviceId: string, public patientId: string) { }
  }

  export class GetNextAppointment {
    static readonly type: string = '[Appointment] Get Next appointment Patient';
    constructor(public clinicId: string, public expertId: string, public patientId: string, public date: string) { }
  }

  export class ResetNextAppointment {
    static readonly type: string = '[Appointment] Reset Next appointment Patient';
    constructor() { }
  }

  export class GetById {
    static readonly type: string = '[Appointment] Get appointment';
    constructor(public appointmentId: string, public concatExpert?: boolean) { }
  }

  export class SetSelectedAppointmentId {
    static readonly type: string = '[Appointment] Set selected appointment id';
    constructor(public appointmentId: string) { }
  }

  export class SetSelectedAppointmentIdObjectives {
    static readonly type: string = '[Appointment] Set selected appointment objectives id';
    constructor(public appointmentId: string) { }
  }

  export class GetAvailableSchedules {
    static readonly type: string = '[Appointment] Get Available Schedules Action';
    constructor(
      public clinicId: string,
      public date: IDateInterface,
      public duration: number,
      public expertId: string,
      public timeZone?: string
    ) { }
  }

  export class Create {
    static readonly type: string = '[Appointment] Create Appointment Schedule';
    constructor(public clinicId: string, public appointmentSchedule: ScheduleAppointmentRequestDTO, public selectAppointment?: boolean) { }
  }

  export class CreateRightNow {
    static readonly type: string = '[Appointment] Create Appointment Schedule Now';
    constructor(public appointmentSchedule: ScheduleAppointmentRequestDTO, public selectAppointment?: boolean) { }
  }

  export class Cancel {
    static readonly type: string = '[Appointment] Cancel Appointment Schedule';
    constructor(public id: string) { }
  }

  export class Reschedule {
    static readonly type: string = '[Appointment] Reschedule Appointment Schedule';
    constructor(public id: string, public appointmentSchedule: Partial<ScheduleAppointmentRequestDTO>) { }
  }

  export class GetAppointmentToAddMealPlan {
    static readonly type: string = '[Appointment] Get Appointment To Add Meal Plan Action';
    constructor(public clinicId: string, public patientId: string, public expertId: string) { }
  }

  export class GetAppointmentURL {
    static readonly type: string = '[Appointment] Get Appointment URL Action';
    constructor(public appointmentId: string) { }
  }

  /**
   * this action is used to add DemoAppointment to appointments list
   */
  export class AddAppointment {
    static readonly type: string = '[Appointment] Add Appointment Action';
    constructor(public appointment: IAppointment) { }
  }

  export class ResetState {
    static readonly type: string = '[Appointment] Reset Appointment State Action';
    constructor() { }
  }

  export class UpdateAppointmentAsCompleted {
    static readonly type: string = '[Appointment] Update Appointment As Completed Action';
    constructor(public appointmentId: string) { }
  }
}

const _DEFAULT_APPOINTMENT_STATE: IRAppointment = {
  appointments: [],
  appointmentsToMealPlan: [],
  appointmentsToAssignTo: [],
  selectedAppointment: null,
  selectedAppointmentObjectives: null,
  scheduleAvailability: null,
  appointmentURL: null
};

@State<IRAppointment>({
  name: 'AppointmentState',
  defaults: cloneDeep(_DEFAULT_APPOINTMENT_STATE)
})

@Injectable()
export class AppointmentState {
  constructor(
    private baseService: BaseService<any>,
    private store: Store,
    private utilitiesService: UtilitiesService,
    private userService: UserService,
  ) { }

  url = environment.SERVER_URL;

  @Selector()
  public static getAppointmentSelected(state: IRAppointment): IAppointment {
    return state.appointments.find((appointment: IAppointment) => appointment._id === state.selectedAppointment);
  }

  @Selector()
  public static getAppointments(state: IRAppointment): IAppointment[] {
    return state.appointments;
  }

  @Selector()
  public static getScheduleAvailability(state: IRAppointment): IGeneratedBlocks {
    return state.scheduleAvailability;
  }

  @Selector()
  public static getNextAppointmentPatient(state: IRAppointment): IAppointment {
    return state.nextAppointmentPatient;
  }

  @Selector()
  public static getAppointmentsToMealPlan(state: IRAppointment): MealPlanAppointmentResponseDTO[] {
    return state.appointmentsToMealPlan;
  }

  @Selector()
  public static getAppointmentsToAssignTo(state: IRAppointment): MealPlanAppointmentResponseDTO[] {
    return state.appointmentsToAssignTo;
  }

  @Selector()
  public static getAppointmentURL(state: IRAppointment): IAppointment {
    return state.appointmentURL;
  }

  @Action(AppointmentActions.Get)
  async getAll(ctx: StateContext<IRAppointment>, action?: AppointmentActions.Get): Promise<void> {
    let url = `${this.url}/services/patients/${action.patientOrExpertId}/appointments`;
    if (action.isExpert) {
      url = `${this.url}/services/experts/${action.patientOrExpertId}/appointments`;
    }
    const filters: any = {
      year: action.year,
      month: action.month,
      timeZone: this.utilitiesService.getTimeZoneBrowser()
    };
    if (action.status && action.isExpert) {
      filters.status = action.status;
    }
    let params: any;
    if (!action.isExpert) {
      params = filters;
    } else {
      params = {
        query: JSON.stringify(filters)
      };
    }
    const response: DataBaseServiceResponse<IAppointment[]> = await firstValueFrom(this.baseService.get(url, void 0, void 0, {
      params,
    }));
    if (response.error) throw new Error(response.message);
    const appointments: IAppointment[] = response.entity.map((appointment: IAppointment) => this.convertAppointmentDate(appointment));
    this.setAppointmentsDate(appointments);
    ctx.patchState({
      appointments,
      selectedAppointment: null,
    });
  }

  @Action(AppointmentActions.GetNextAppointment)
  async getNextAppointmentAction(ctx: StateContext<IRAppointment>, action?: AppointmentActions.GetNextAppointment): Promise<void> {
    const url = `${this.url}/services/patients/${action.patientId}/experts/${action.expertId}/next-appointment`;
    const response: DataBaseServiceResponse<IAppointment> = await firstValueFrom(this.baseService.get(url, void 0, void 0, {
      params: {
        date: action.date
      }
    }));
    if (response.error) {
      return;
      // throw new Error(response.message);
    }

    const appointments: IAppointment[] = [response.entity];
    this.setAppointmentsDate(appointments);
    ctx.patchState({
      nextAppointmentPatient: appointments[0]
    });
  }

  @Action(AppointmentActions.ResetNextAppointment)
  async resetNextAppointmentAction(ctx: StateContext<IRAppointment>): Promise<void> {
    ctx.patchState({
      nextAppointmentPatient: null
    });
  }

  @Action(AppointmentActions.GetById)
  async getAppointment(ctx: StateContext<IRAppointment>, action: AppointmentActions.GetById): Promise<void> {
    const response: DataBaseServiceResponse<IAppointment> = await firstValueFrom((this.baseService.get(`${this.url}/appointments/${action.appointmentId}`)));
    if (response.error) throw new Error(response.message);
    const appointment: IAppointment = response.entity as IAppointment;
    if (!action.concatExpert) {
      ctx.patchState({
        selectedAppointment: action.appointmentId,
        appointments: [response.entity],
      });
      return;
    }
    // Para concatenar el experto solamente al appointmentSeleccionado
    const state = cloneDeep(ctx.getState());
    const foundIndex = state.appointments.findIndex((n) => n._id === action.appointmentId);
    if (foundIndex !== -1) {
      const appointmentData = state.appointments[foundIndex];
      appointmentData.expert = response.entity.expert; // concatenar el experto obtenido en el response
      appointmentData.metadata = response.entity.metadata;
      // colocar nuevamente el state
      state.appointments[foundIndex] = this.convertAppointmentDate(appointmentData);
    } else {
      state.appointments.push(this.convertAppointmentDate(appointment));
    }
    ctx.setState(state);
  }

  @Action(AppointmentActions.GetAppointmentURL)
  async GetAppointmentURLAction(ctx: StateContext<IRAppointment>, action: AppointmentActions.GetAppointmentURL): Promise<void> {
    const response: DataBaseServiceResponse<IAppointment> = await firstValueFrom((this.baseService.get(`${this.url}/appointments/${action.appointmentId}`)));
    if (response.error) throw new Error(response.message);
    const appointment: IAppointment = response.entity as IAppointment;
    ctx.patchState({
      appointmentURL: appointment
    });
  }

  @Action(AppointmentActions.GetAvailableSchedules)
  async getAvailableSchedulesAction(ctx: StateContext<IRAppointment>, action?: AppointmentActions.GetAvailableSchedules): Promise<void> {
    const path = `${this.url}/clinics/${action.clinicId}/experts/${action.expertId}/schedules/available`;
    const paramsData: Params = {
      date: JSON.stringify(action.date),
      duration: action.duration,
    };
    if (action.timeZone) {
      paramsData.timeZoneOverride = action.timeZone;
    }
    const response: DataBaseServiceResponse<IGeneratedBlocks> = await firstValueFrom(this.baseService.get(
      path, void 0, void 0,
      {
        params: paramsData
      }));
    if (response.error) throw new Error(response.message);
    ctx.patchState({
      scheduleAvailability: response.entity,
    });
  }

  @Action(AppointmentActions.SetSelectedAppointmentId)
  async setSelectedAppointmentIdAction(ctx: StateContext<IRAppointment>, action?: AppointmentActions.SetSelectedAppointmentId): Promise<void> {
    if (!action.appointmentId) {
      return;
    }
    ctx.patchState({
      selectedAppointment: action.appointmentId,
    });
  }

  @Action(AppointmentActions.SetSelectedAppointmentIdObjectives)
  async setSelectedAppointmentIdObjectivesAction(ctx: StateContext<IRAppointment>,
                                                 action?: AppointmentActions.SetSelectedAppointmentIdObjectives): Promise<void> {
    if (!action.appointmentId) {
      return;
    }
    ctx.patchState({
      selectedAppointmentObjectives: action.appointmentId,
    });
  }

  @Action(AppointmentActions.Create)
  async createAppointmentScheduleAction(ctx: StateContext<IRAppointment>,
                                        action: AppointmentActions.Create): Promise<void> {
    const response: DataBaseServiceResponse<IAppointment> = await firstValueFrom((
      this.baseService.post(`${this.url}/appointments/schedule`, action.appointmentSchedule)
    ));
    if (response.error) throw new Error(response.message);
    const appointments: IAppointment[] = [...ctx.getState().appointments, response.entity];
    ctx.patchState({
      appointments
    });
    if (action.selectAppointment) {
      ctx.patchState({
        selectedAppointment: response.entity._id
      });
    }
  }

  @Action(AppointmentActions.CreateRightNow)
  async createAppointmentScheduleNowAction(ctx: StateContext<IRAppointment>, action: AppointmentActions.CreateRightNow): Promise<void> {
    const response: DataBaseServiceResponse<IAppointment> = await firstValueFrom((
      this.baseService.post(`${this.url}/appointments/schedule-now`, action.appointmentSchedule)
    ));
    if (response.error) throw new Error(response.message);
    const appointments: IAppointment[] = [
      ...ctx.getState().appointments, this.convertAppointmentDate(response.entity),
    ];
    ctx.patchState({
      appointments
    });
    if (action.selectAppointment) {
      ctx.patchState({
        selectedAppointment: response.entity._id
      });
    }
  }

  @Action(AppointmentActions.GetAppointmentToAddMealPlan)
  async getAppointmentToAddMealPlanAction(ctx: StateContext<IRAppointment>, action: AppointmentActions.GetAppointmentToAddMealPlan): Promise<void> {
    const response: DataBaseServiceResponse<MealPlanAppointmentResponseDTO[]> = await firstValueFrom(
      (this.baseService.get(`${this.url}/patients/${action.patientId}/experts/${action.expertId}/meal-plans/appointments`)));
    if (response.error) throw new Error(response.message);
    response.entity.forEach((item: MealPlanAppointmentResponseDTO) =>
      item.startDate.date = DateHelper.convertUtcToZonedTime(new Date(item.startDate.date), this.userService.getUserTimeZone())
    );
    ctx.patchState({
      appointmentsToMealPlan: response.entity
    });
  }

  @Action(AppointmentActions.UpdateAppointmentAsCompleted)
  async updateAppointmentAsCompletedAction(ctx: StateContext<IRAppointment>, action: AppointmentActions.UpdateAppointmentAsCompleted): Promise<void> {
    const appointments: IAppointment[] = cloneDeep(ctx.getState().appointments);
    const appointmentIndex: number = appointments.findIndex(item => item._id === action.appointmentId);
    if (appointmentIndex !== -1) {
      appointments[appointmentIndex].status = AppointmentStatusEnum.COMPLETED;
      ctx.patchState({
        appointments: [... appointments]
      });
    }
  }

  @Action(AppointmentActions.GetToAssign)
  async getAppointmentsToAssignAction(ctx: StateContext<IRAppointment>, action: AppointmentActions.GetToAssign): Promise<void> {
    const url = `${this.url}/services/${action.serviceId}/patients/${action.patientId}/appointments`;
    const response: DataBaseServiceResponse<MealPlanAppointmentResponseDTO[]> = await firstValueFrom((this.baseService.get(url)));
    if (response.error) throw new Error(response.message);
    response.entity.forEach((item: MealPlanAppointmentResponseDTO) =>
      item.startDate.date = DateHelper.convertUtcToZonedTime(new Date(item.startDate.date), this.userService.getUserTimeZone())
    );
    ctx.patchState({
      appointmentsToAssignTo: response.entity
    });
  }

  @Action(AppointmentActions.AddAppointment)
  async addAppointmentAction(ctx: StateContext<IRAppointment>, {appointment}: AppointmentActions.AddAppointment): Promise<void> {
    const appointments: IAppointment[] = cloneDeep(ctx.getState().appointments ?? []);
    const index: number = appointments.findIndex(({_id}: IAppointment) => _id === appointment._id);
    if (index === -1) {
      appointments.push(appointment);
    } else {
      appointments[index] = appointment;
    }
    ctx.patchState({appointments});
  }

  @Action(AppointmentActions.Reschedule)
  async rescheduleAppointmentScheduleAction(ctx: StateContext<IRAppointment>, action: AppointmentActions.Reschedule): Promise<void> {
    const response: DataBaseServiceResponse<IAppointment> = await firstValueFrom(
      this.baseService.patch(`${this.url}/appointments/${action.id}/reschedule`, action.appointmentSchedule)
    );
    if (response.error) throw new Error(response.message);
    const appointments: IAppointment[] = cloneDeep(ctx.getState().appointments);
    const appointmentIndex: number = appointments.findIndex(appointment => appointment._id === action.id);
    if (appointmentIndex !== -1) {
      appointments[appointmentIndex] = response.entity;
      ctx.patchState({
        appointments,
        selectedAppointment: action.id
      });
    }
  }

  @Action(AppointmentActions.Cancel)
  async cancelAppointmentScheduleAction(ctx: StateContext<IRAppointment>, action: AppointmentActions.Cancel): Promise<void> {
    const response: DataBaseServiceResponse<IAppointment> = await firstValueFrom((
      this.baseService.post(`${this.url}/appointments/${action.id}/cancel`, void 0)
    ));
    if (response.error) throw new Error(response.message);
    // saber cual elemento fue el modificado para reemplazarlo en el arreglo dentro del state
    const appointments: IAppointment[] = cloneDeep(ctx.getState().appointments);
    // tslint:disable:variable-name
    const appointmentIndex: number = appointments.findIndex(_appointment => _appointment._id === action.id);
    if (appointmentIndex !== -1) {
      appointments[appointmentIndex].status = AppointmentStatusEnum.CANCELLED;
      ctx.patchState({
        appointments
      });
    }
  }

  @Action(AppointmentActions.ResetState)
  async resetAppointmentStateAction(ctx: StateContext<IRAppointment>): Promise<void> {
    ctx.setState(cloneDeep(_DEFAULT_APPOINTMENT_STATE));
  }

  setAppointmentsDate(appointments: IAppointment[]): void {
    appointments.forEach((item: IAppointment) => {
      const startDate = DateHelper.convertUtcToZonedTime(new Date(item.startDate.date), this.userService.getUserTimeZone());
      const endDate = DateHelper.convertUtcToZonedTime(new Date(item.endDate.date), this.userService.getUserTimeZone());
      item.startDate.year = startDate.getFullYear();
      item.startDate.month = startDate.getMonth();
      item.startDate.day = startDate.getDate();
      item.startDate.hour = endDate.getHours();
      item.endDate.year = endDate.getFullYear();
      item.endDate.month = endDate.getMonth();
      item.endDate.day = endDate.getDate();
      item.endDate.hour = endDate.getHours();
    });
  }

  convertAppointmentDate(appointment: IAppointment): IAppointment {
    const startDate = DateHelper.convertUtcToZonedTime(appointment.startDate.date, this.userService.getUserTimeZone());
    const endDate = DateHelper.convertUtcToZonedTime(appointment.endDate.date, this.userService.getUserTimeZone());
    return {
      ...appointment,
      startDate: {
        ...appointment.startDate,
        date: startDate,
        year: startDate.getFullYear(),
        month: startDate.getMonth(),
        day: startDate.getDate(),
        hour: startDate.getHours(),
        minute: startDate.getMinutes(),
        timestamp: startDate.getTime(),
      },
      endDate: {
        ...appointment.endDate,
        date: endDate,
        year: endDate.getFullYear(),
        month: endDate.getMonth(),
        day: endDate.getDate(),
        hour: endDate.getHours(),
        minute: endDate.getMinutes(),
        timestamp: endDate.getTime(),
      }
    };
  }
}

